import React, { useContext, useState } from 'react'
import { useApolloClient } from '@apollo/client/react'
import { useIntl } from 'react-intl'
import Linkify from 'react-linkify'

import { towingServiceTypeOptions } from '../../../common/options'
import { Maybe, TowingOrderStatus, TowingOrderType, TypeIdentifier, User } from '../../../common/types'
import { getStatus } from '../../../components/JobStatus'
import { getDeliverable, getJobTypeDescription } from '../../../components/JobTypeDescription'
import { FlexContainer, Page, Section, SectionTextColumn, Text } from '../../../components/layout'
import { SectionBody, SectionHeader } from '../../../components/layout/section'
import LinkButton from '../../../components/LinkButton'
import { Modal } from '../../../components/Modal'
import { parseAddress, parseLocationQueryString } from '../../../components/Parsers'
import { PhoneNumber } from '../../../components/PhoneNumber'
import { useHistoryPush } from '../../../components/router'
import { Styling } from '../../../components/Styling'
import { TopNavigation } from '../../../components/TopNavigation/TopNavigation'
import { BuildJobDetails } from '../../../components/TowingCommon/JobDetails'
import { SummaryHeader } from '../../../components/TowingCommon/SummaryHeader'
import VehicleSearchModal from '../../../components/VehicleSearchModal'
import {
  transformDrive,
  transformFuel,
  transformTransmission,
  transformVehicleClass,
} from '../../../components/VehicleSearchModal/transformers'
import { StateStore } from '../../../containers/StoreProvider'
import { toMultilineText } from '../../../util/common'
import { getStationByIdFromSettings } from '../../Parameters/station'
import { getOperatorLabelFromId } from '../../Parameters/user'
import { RejectTowingOrder } from '../components/RejectOrder'
import { RejectType } from '../components/RejectOrder/types'
import { messages } from '../messages'
import { StatusChangeButtons } from './StatusChangeButtons'
import { TowingOrder, TowingOrderView } from './types'
import { WarningBanner } from '../../../components/TowingCommon/WarningBanner'
import { getRepairShop } from './getRepairShop'
import { MapLink } from '../../../components/MapLink'

import { ClipboardButton } from '../../../components/ClipboardButton'
import { VehicleInfoDataSource } from '../../../components/VehicleDetails/types'
import { TraficomInfoText } from '../../../components/VehicleDetails/TraficomInfoText'
import { useRepairShops } from '../../TowingRecordPage/components/routes/subforms/FinishRoute/options'
import { formatMessageSafe } from '../../../util/translateInputOptions'
import { useSettings } from '../../Parameters'
import { JobItemsReadOnlyList } from '../../../components/TowingRecordItems/JobItemsReadOnlyList'
import { splitOrderItems } from '../utils/splitOrderItems'
import { Button } from '../../../components/Button'
import { SendSmsDialog } from '../../../components/SendSmsDialog/SendSmsDialog'
import { canShowSendSmsOnOrder } from '../../../components/SendSmsDialog/canShowSendSms'

interface TowingOrderFormProps {
  towingOrder: TowingOrder
  refetchData: () => void
}

export const ViewTowingOrderContent: React.FunctionComponent<TowingOrderFormProps> = ({
  refetchData,
  towingOrder,
}) => {
  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)
  const [modalVisibility, setModalVisibility] = useState<boolean>(false)
  const [modalMessage, setModalMessage] = useState<string>('')
  const [showSendSmsDialog, setShowSendSmsDialog] = useState(false)
  const [view, setView] = useState<TowingOrderView>('view-towing-order')
  const [rejectType, setRejectType] = useState<RejectType>(null)
  const historyPush = useHistoryPush()
  const client = useApolloClient()
  const { state } = useContext(StateStore)
  const settings = useSettings()
  const { repairShops } = useRepairShops({ skip: towingOrder.typeIdentifier !== TypeIdentifier.autoliitto})
  const repairShop = getRepairShop(repairShops, towingOrder)
  const { orderItems, discountItem } = splitOrderItems(
    towingOrder.orderItems ?? [],
    towingOrder.discountItem ?? null
  )
  const hasOrderItems = orderItems.length > 0 || discountItem !== null

  const allowEdit = (user: Maybe<User>) => {
    if (!user) {
      return false
    }
    return true
  }

  const showStatusButtons = allowEdit(state.currentUser)
  const showEditButton = towingOrder.status !== TowingOrderStatus.pending_acceptance
  const showSendSms = canShowSendSmsOnOrder(state.currentUser, towingOrder)

  const showTowingOrder = view === 'view-towing-order'
  const showRejectModal = view === 'view-reject-modal'
  const showVehicleInfo = view === 'view-vehicle-info'



  const setRejectView = (type: RejectType) => {
    setRejectType(type)
    setView('view-reject-modal')
  }

  const handleVehicleInfoLinkClick = () => {
    if (towingOrder.vehicleInfoCheckId) {
      setView('view-vehicle-info')
    } else {
      historyPush(`/towing-order/${towingOrder.id}/edit/atj`)
    }
  }

  const vehicleInfoCheck = towingOrder.vehicleInfoCheck
  const isTrafiVehicleData = vehicleInfoCheck && vehicleInfoCheck.searchMeta && vehicleInfoCheck.searchMeta.dataSource === VehicleInfoDataSource.traficom ? true : false

  return (
    <Page noPadding>
      {showRejectModal && (
        <RejectTowingOrder
          onClose={() => setView('view-towing-order')}
          towingOrder={towingOrder}
          rejectType={rejectType}
        />
      )}

      {showVehicleInfo && (
        <VehicleSearchModal
          setValue={() => {} }
          getValue={() => {  }}
          vehicleRegistration={towingOrder.vehicleDetails.registrationNumber}
          vehicleInfoCheckId={towingOrder.vehicleInfoCheckId}
          close={() => setView('view-towing-order')}
          client={client}
        />
      )}

      {showTowingOrder && (
        <>
          {modalVisibility && (
            <Modal
              label={formatMessage(messages.view_statusUpdateModalLabel)}
              message={modalMessage}
              close={() => setModalVisibility(false)}
            />
          )}
          {towingOrder.locked && (
            <WarningBanner
              title={formatMessage(messages.view_lockedTitle)}
              message={formatMessage(messages.view_lockedMessage, { lockedBy: towingOrder.locked })}
              refetch={refetchData}
            />
          )}
          <TopNavigation
            address="/towing-jobs"
            label={formatMessage(messages.view_navLabel)}
            editAction={showEditButton ? () => historyPush(`/towing-order/${towingOrder.id}/edit`) : undefined}
            editAddress={`/towing-order/${towingOrder.id}/edit`}
            // editAddress={showEditButton ? `/towing-order/${towingOrder.id}/edit` : undefined}
            editLabel={formatMessage(messages.view_navEditLabel)}
            disableEdit={!!towingOrder.locked}
          />
          <SummaryHeader
            status={getStatus(
              intl,
              towingOrder.status,
              getOperatorLabelFromId(
                towingOrder.operatorId,
                state.currentUser,
                settings ? settings.operators : null,
                id => formatMessage({ id })
              ),
              towingOrder.visibility
            )}
            jobTypeDescription={getJobTypeDescription(
              intl,
              towingOrder.jobDetails.serviceType,
              getStationByIdFromSettings(towingOrder.stationId, settings),
              towingOrder.label,
              towingOrder.typeIdentifier,
              getDeliverable(towingOrder.sosServiceOrder)
            )}
            type={
              towingOrder.jobDetails.serviceType
                ? formatMessage({ id: towingServiceTypeOptions.options[towingOrder.jobDetails.serviceType] })
                : null
            }
            station={getStationByIdFromSettings(towingOrder.stationId, settings)}
            vehicleDetails={towingOrder.vehicleDetails}
            jobDetails={towingOrder.jobDetails}
            towingOrder={towingOrder}
            reFetchData={refetchData}
          />
          <Styling padding="0 2rem" paddingMobile="0.5rem" spacing="extra">
            {showStatusButtons && !towingOrder.locked && (
              <StatusChangeButtons
                status={towingOrder.status}
                type={towingOrder.type as TowingOrderType}
                towingOrderId={towingOrder.id}
                handleSuccess={(msg: string) => {
                  setModalMessage(msg)
                  refetchData()
                }}
                handleError={(msg: string) => {
                  setModalMessage(formatMessage(messages.view_statusUpdateFailed, { error: msg }))
                  setModalVisibility(true)
                }}
                openRejectPage={setRejectView}
                towingOrder={towingOrder}
              />
            )}
          </Styling>
          {showSendSms && (
            <Styling padding="0 2rem" paddingMobile="0.5rem" spacing="extra">
              <Button
                category="on"
                label="SMS"
                size="l"
                fontWeight="normal"
                onClick={() => setShowSendSmsDialog(true)}
              />
            </Styling>
          )}
          {showSendSmsDialog && (
            <SendSmsDialog
              entityType="order"
              entityId={towingOrder.id}
              ordererPhone={towingOrder.orderer?.phone}
              onClose={() => setShowSendSmsDialog(false)}
            />
          )}
          <Styling padding="0 2rem" paddingMobile="0 0.5rem">
            <Section gap="2rem" justify="text" columns={2}>
              <SectionTextColumn width="100%">
                <SectionHeader>
                  <Text size="medium">{formatMessage(messages.view_vehicleDetailsHeader)}</Text>
                  <LinkButton onClick={handleVehicleInfoLinkClick}>{formatMessage(messages.view_atjLinkLabel)}</LinkButton>
                </SectionHeader>
                <SectionBody>
                  {vehicleInfoCheck ? (
                    <>
                      <Text size="medium">{transformVehicleClass(vehicleInfoCheck.vehicle.vehicleClass)}</Text>
                      <Text size="medium">
                        {`${transformTransmission(vehicleInfoCheck.vehicle.transmission)} / ${transformDrive(
                          vehicleInfoCheck.vehicle.driveTrain
                        )} / ${transformFuel(vehicleInfoCheck.vehicle.fuel)}`}
                      </Text>
                      <TraficomInfoText show={isTrafiVehicleData} />
                    </>
                  ) : (
                    <Text size="medium">{towingOrder.vehicleDetails.vehicleClass}</Text>
                  )}
                 
                </SectionBody>
              </SectionTextColumn>
              <SectionTextColumn width="100%">
                <SectionHeader>
                  <Text size="medium">{formatMessage(messages.view_customerHeader)}</Text>
                </SectionHeader>
                <SectionBody>
                    {towingOrder.orderer && towingOrder.orderer.__typename === 'PersonCustomer' && (
                      <Text size="medium">{`${towingOrder.orderer.firstName} ${towingOrder.orderer.lastName}`}</Text>
                    )}
                    {towingOrder.orderer && towingOrder.orderer.__typename === 'CompanyCustomer' && (
                      <Text size="medium">{`${towingOrder.orderer.companyName}`}</Text>
                    )}
                    {towingOrder.orderer && towingOrder.orderer.phone ? (
                        <PhoneNumber size="medium" phone={towingOrder.orderer.phone} />
                      ) : (
                        <Text style={{ visibility: "hidden" }} size="medium">-</Text> 
                      )
                    }
                </SectionBody>
              </SectionTextColumn>
              <SectionTextColumn width="100%">
                <SectionHeader>
                  <Text size="medium">{formatMessage(messages.from)}</Text>
                </SectionHeader>
                <SectionBody>
                <FlexContainer style={{gap: "10px"}}>
                  <MapLink size="medium" queryString={parseLocationQueryString(towingOrder.from)} address={parseAddress(towingOrder.from.address, towingOrder.from.city)} />
               <ClipboardButton 
                  copy={towingOrder.from.coords?.lat && towingOrder.from.coords?.long 
                    ? `${towingOrder.from.coords.lat}, ${towingOrder.from.coords.long}`
                    : parseAddress(towingOrder.from.address, towingOrder.from.city)
                  }
                />
                </FlexContainer>
                <Text size="medium">{`${towingOrder.from.description}`}</Text>
                </SectionBody>
              </SectionTextColumn>
              <SectionTextColumn width="100%">
                <SectionHeader>
                  <Text size="medium">{formatMessage(messages.view_toHeader)}</Text>
                </SectionHeader>
                {repairShop &&
                towingOrder.typeIdentifier === TypeIdentifier.autoliitto &&
                towingOrder.jobDetails.serviceType !== 'towing' ? (
                  <SectionBody>
                    <Text size="medium" fontWeight="bold">
                      {formatMessage(messages.view_repairShopWarning)}
                    </Text>
                    <FlexContainer style={{gap: "10px"}}>
                      <MapLink
                        size="medium"
                        queryString={parseAddress(repairShop.location.address, repairShop.location.city)}
                        address={parseAddress(repairShop.location.address, repairShop.location.city)}
                      />
                      <ClipboardButton copy={parseAddress(repairShop.location.address, repairShop.location.city)}/>
                    </FlexContainer>
                    <Text size="medium">{repairShop.name}</Text>
                  </SectionBody>
                ) : (
                  <SectionBody>
                    <FlexContainer style={{gap: "10px"}}>
                      <MapLink size="medium" queryString={parseLocationQueryString(towingOrder.to)}  address={parseAddress(towingOrder.to.address, towingOrder.to.city)} />
                      <ClipboardButton copy={towingOrder.to.coords?.lat && towingOrder.to.coords?.long 
                        ? `${towingOrder.to.coords.lat}, ${towingOrder.to.coords.long}`
                        : parseAddress(towingOrder.to.address, towingOrder.to.city)
                      }/>
                    </FlexContainer>
                    <Text size="medium">{`${towingOrder.to.description}`}</Text>
                  </SectionBody>
                )}
              </SectionTextColumn>
              <BuildJobDetails towingObject={towingOrder} />
              {hasOrderItems && (
                <SectionTextColumn width="100%">
                  <SectionHeader>
                    <Text size="medium">{formatMessage(messages.view_paymentLinesHeader)}</Text>
                  </SectionHeader>
                  <SectionBody>
                    <JobItemsReadOnlyList
                      jobItems={orderItems}
                      discountItem={discountItem}
                      itemsIncludeVat={towingOrder.itemsIncludeVat ?? false}
                    />
                  </SectionBody>
                </SectionTextColumn>
              )}
              {towingOrder.typeIdentifier !== TypeIdentifier.sos && (
                <SectionTextColumn width="100%">
                  <SectionHeader>
                    <Text size="medium">{formatMessage(messages.view_additionalInfoHeader)}</Text>
                  </SectionHeader>
                  <SectionBody>
                    <Linkify>{toMultilineText(towingOrder.jobDetails.additionalInfo)}</Linkify>
                  </SectionBody>
                </SectionTextColumn>
              )}
              <SectionTextColumn width="100%">
                <SectionHeader>
                  <Text size="medium">{formatMessage(messages.view_notesHeader)}</Text>
                </SectionHeader>
                <SectionBody>
                  <Linkify>{toMultilineText(towingOrder.jobDetails.notes)}</Linkify>
                </SectionBody>
              </SectionTextColumn>
            </Section>
          </Styling>
        </>
      )}
    </Page>
  )
}
